import createStyles from '@mui/styles/createStyles';
import { expandTheme } from 'utils/theme-util';
import { isLightTheme } from 'lib/themes/theme';

const styles = (theme: any) => {
  const { palette, font } = expandTheme(theme);

  const headerColumnPadding = {
    paddingTop: theme.spacing(),
    paddingBottom: theme.spacing(),
  };
  const actionIcon = {
    width: '21px',
    padding: '2px',
    cursor: 'pointer',
    transform: 'translateX(-15%) translateY(-2px)',
  };

  return createStyles({
    noteCountText: {
      color: palette.primary.formItem,
      fontWeight: 500,
    },
    table: {
      borderRadius: theme.spacing(0.5),
      minWidth: theme.spacing(80),
      border: `1px solid ${palette.primary.grey25}`,
      backgroundColor: isLightTheme()
        ? theme.palette.background.default
        : theme.palette.primary.background,
      color: isLightTheme() ? palette.primary.formItem : palette.primary.white,
    },
    filterDateLabel: {
      fontSize: '11px',
      fontWeight: 400,
      color: isLightTheme() ? palette.primary.formItem : palette.primary.white,
    },
    fieldLabel: {
      fontSize: '11px',
      fontWeight: 400,
      minHeight: 'inherit',
      WebkitTransform: 'inherit',
      lineHeight: 1.66,
      color: isLightTheme() ? palette.primary.grey12 : palette.primary.white,
    },
    filterSpacing: {
      paddingRight: '20px',
    },
    filterSpacingPeriod: {
      paddingRight: '20px',
      paddingTop: '25px',
    },
    taskTypeCell: {
      'color': isLightTheme() ? palette.primary.patina : palette.primary.white,
      '&:hover': { cursor: 'pointer' },
    },
    filterTherapyStyling: {
      'paddingRight': '20px',
      '& > div > div > div > div': {
        marginTop: '12px',
      },
      '& label': {
        fontSize: '11px',
        fontWeight: 400,
        minHeight: 'inherit',
        WebkitTransform: 'inherit',
        lineHeight: 1.66,
        color: isLightTheme() ? palette.primary.grey12 : palette.primary.formItem,
      },
    },
    filterTaskStyling: {
      'paddingRight': '20px',
      '& > div > div > div > div': {
        marginTop: '12px',
      },
      '& label': {
        fontSize: '11px',
        fontWeight: 400,
        minHeight: 'inherit',
        WebkitTransform: 'inherit',
        lineHeight: 1.66,
        color: isLightTheme() ? palette.primary.grey12 : palette.primary.formItem,
      },
    },
    header: {
      backgroundColor: isLightTheme() ? palette.primary.grey2 : palette.primary.trellisBlue,
      height: 10,
    },
    oddRow: {
      backgroundColor: palette.primary.grey24,
      color: isLightTheme() ? palette.primary.formItem : palette.primary.formItem,
    },
    evenRow: {
      backgroundColor: palette.primary.grey14,
      color: isLightTheme() ? palette.primary.formItem : palette.primary.formItem,
    },
    completedRow: {
      backgroundColor: isLightTheme() ? palette.primary.patinaWhite : palette.primary.darkGreen,
      color: isLightTheme() ? palette.primary.formItem : palette.text.success,
    },
    highlightRow: {
      backgroundColor: palette.primary.trellisLightBlue,
      color: isLightTheme() ? palette.primary.formItem : palette.primary.formItem,
    },
    overDueRow: {
      backgroundColor: isLightTheme() ? palette.primary.white : palette.primary.grey13,
    },
    actionIconTaskInProgress: {
      ...actionIcon,
      '& *': {
        fill: palette.primary.rollingStone,
      },
    },
    actionIconTaskCompleted: {
      ...actionIcon,
      '& *': {
        fill: palette.primary.rollingStone,
        opacity: 0.8,
      },
    },
    headerColumn: {
      ...headerColumnPadding,
      'color': isLightTheme() ? palette.primary.formItem : palette.primary.white,
      '& *': {
        'color': isLightTheme() ? palette.primary.formItem : palette.primary.white,
        '&.Mui-active': {
          color: isLightTheme() ? palette.primary.formItem : palette.primary.white,
        },
      },
    },
    headerNarrowColumn: {
      ...headerColumnPadding,
      width: theme.spacing(1),
      color: isLightTheme() ? palette.primary.formItem : palette.primary.white,
    },
    headerNoteColumn: {
      ...headerColumnPadding,
      minWidth: theme.spacing(37.5),
      width: theme.spacing(37.5),
      color: isLightTheme() ? palette.primary.formItem : palette.primary.white,
    },
    noteColumn: {
      backgroundColor: palette.primary.grey2,
      borderRadius: theme.spacing(0.25),
      color: palette.primary.formItem,
    },
    emptyNoteColumn: {
      width: theme.spacing(38.75),
      height: theme.spacing(5.4375),
      backgroundColor: palette.primary.grey2,
      color: palette.primary.formItem,
    },
    outstandingText: {
      color: palette.primary.valenciaRed,
      fontWeight: 'bold',
      display: 'flex',
      alignItems: 'center',
    },
    inProgressText: {
      display: 'flex',
      alignItems: 'center',
      gap: '5px',
      color: isLightTheme() ? palette.primary.trellisBlue : palette.primary.trellisBlue,
      fontWeight: 'bold',
    },
    dcPromIcon: {
      color: isLightTheme() ? palette.primary.rollingStone : palette.primary.formItem,
      marginLeft: '5px',
    },
    completedText: {
      display: 'flex',
      gap: '5px',
      color: isLightTheme() ? palette.primary.patina : palette.primary.formItem,
      fontWeight: 'bold',
    },
    therapyNameFull: {
      fontWeight: 'bold',
      fontSize: font.smallFontSize,
      color: isLightTheme() ? palette.primary.formItem : palette.primary.formItem,
    },
    dosingIconDisabled: {
      '& path': {
        opacity: '0.20',
      },
      'color': isLightTheme() ? palette.primary.rollingStone : palette.primary.formItem,
    },
    prnIcon: {
      width: '25px',
    },
    dispensingIconDisabled: {
      '& path': {
        opacity: '0.20',
      },
    },
    fancyTooltip: {
      fontSize: '0.58rem',
      fontWeight: 'bold',
    },
    partialTaskSelectionSet: {
      color: 'grey !important',
    },
    showFormButton: {
      margin: 'auto',
    },
    messageContainer: {
      textAlign: 'center',
      height: '100px',
      verticalAlign: 'middle',
      lineHeight: '100px',
      fontSize: '20px',
      backgroundColor: palette.primary.trellisLightBlue,
    },
    therapyEllipsis: {
      cursor: 'default',
    },
    diagnosisText: {
      fontSize: '11px',
      color: palette.primary.white,
    },
    diagnosisTextBold: {
      fontSize: '11px',
      fontWeight: 'bold',
      color: palette.primary.white,
    },
    therapyTag: {
      'borderRadius': '3px',
      'color': palette.primary.white,
      'padding': '3px 4px',
      'marginLeft': '4px',
      'backgroundColor': palette.primary.success,
      'fontWeight': font.weight.thick,
      'fontSize': '0.6rem',
      'lineHeight': 'normal',
      'position': 'relative',
      'top': '-1px',
      '&.CDM': {
        backgroundColor: palette.primary.yellow,
        color: palette.primary.deepBlue,
      },
      '&.OTF': {
        color: palette.primary.white,
        backgroundColor: palette.primary.deepBlue,
      },
    },
    plusSignButton: {
      textAlign: 'center',
      lineHeight: '20px',
      cursor: 'pointer',
      height: '20px',
      width: '20px',
      backgroundColor: '#5D87AF',
      color: 'white',
      borderRadius: '5px',
    },
    taskTabTaskContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      width: '100%',
    },
    tasktabLabelIcon: {
      flex: 1,
      textAlign: 'center',
    },
    dateIcon: {
      color: isLightTheme() ? palette.primary.grey12 : palette.primary.formItem,
    },
  });
};

const Styles = {
  addTaskButton: {
    'backgroundColor': 'primary.background',
    'border': '1px solid #C4C4C4',
    'borderRadius': '4px',
    'boxShaddow': 'none',
    'color': 'primary.formItem',
    'boxShadow': 'none',
    '&:hover': {
      backgroundColor: 'primary.background',
      border: '1px solid #C4C4C4',
      borderRadius: '4px',
      color: 'primary.steelGrey',
      boxShaddow: 'none',
      boxShadow: 'none',
    },
  },
};

export { styles, Styles };
